import { useState, ChangeEvent, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';
import { usePublishChatContext } from '@/features/chatbot/usePublishChatContext';
import { computeDataProfile } from '@/features/chatbot/computeDataProfile';
import type { ChatField } from '@/features/chatbot/types';
import { DownloadModal } from './components/DownloadModal';
import { validateFile, sanitizeFileName, renderWarning, detectDelimiter, type WarningInfo } from './utils/csvValidation';
import { parseRows } from './utils/parseCsvText';
import { buildCsvContent } from './utils/buildCsvContent';
import { useFileSizeLimit } from '@/hooks/useFileSizeLimit';
import DragDropFileUpload from '@/features/data-upload/components/DragDropFileUpload';

interface CsvRow {
  columns: string[]; // All column values
  fileName: string; // Track which file this row came from
}

// Performance constants
const SEARCH_INPUT_THRESHOLD = 100; // Show search input instead of dropdown if more than this many values
const DEBOUNCE_DELAY_MS = 500; // Delay before applying filter changes
const MAX_SUGGESTIONS = 50; // Maximum number of suggestions to show in search dropdown
const FILE_PROCESSING_DELAY_MS = 300; // Delay before processing uploaded files

// Helper function to compute unique values for each column
function computeColumnUniqueValues(data: CsvRow[]): Record<number, string[]> {
  if (data.length === 0) return {};

  const maxCols = data.reduce((max, row) => Math.max(max, row.columns.length), 0);
  const uniqueValuesPerColumn: Record<number, string[]> = {};

  for (let colIndex = 0; colIndex < maxCols; colIndex++) {
    // Extract ALL unique values for proper search functionality
    const uniqueSet = new Set<string>();

    for (const row of data) {
      const value = row.columns[colIndex];
      if (value) {
        uniqueSet.add(value);
      }
    }

    // Convert to array and sort only if needed for dropdown (≤100 values)
    // For search input (>100 values), we'll filter and sort on-demand
    const uniqueArray = Array.from(uniqueSet);
    uniqueValuesPerColumn[colIndex] = uniqueArray.length <= SEARCH_INPUT_THRESHOLD
      ? uniqueArray.sort()
      : uniqueArray; // Don't sort large arrays upfront for performance
  }

  return uniqueValuesPerColumn;
}

interface UploadedFile {
  name: string;
  size: number;
  rowCount: number;
  uploadedAt: string;
}

export default function CsvFilterPage() {
  const { t } = useTranslation('csvFilter');
  const { maxFileSizeMB } = useFileSizeLimit();
  const [originalData, setOriginalData] = useState<CsvRow[]>([]);
  const [filteredData, setFilteredData] = useState<CsvRow[]>([]);
  const [columnFilters, setColumnFilters] = useState<Record<number, string>>({}); // Column index -> selected value
  const [columnUniqueValues, setColumnUniqueValues] = useState<Record<number, string[]>>({}); // Column index -> unique values
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingProgress, setLoadingProgress] = useState<number>(0);
  const [currentFileName, setCurrentFileName] = useState<string>('');
  const [columnCount, setColumnCount] = useState<number>(0);
  const [csvDelimiter, setCsvDelimiter] = useState<string>(';');

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(100);

  // Modal state for download
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState<boolean>(false);

  // Debounced filter state
  const [pendingFilters, setPendingFilters] = useState<Record<number, string>>({});

  // Search input state for columns with many values
  const [searchInputs, setSearchInputs] = useState<Record<number, string>>({});
  const [showSuggestions, setShowSuggestions] = useState<Record<number, boolean>>({});

  // File name filter state
  const [fileNameFilter, setFileNameFilter] = useState<string>('');

  // Validation state
  const [validationError, setValidationError] = useState<string>('');
  const [validationWarnings, setValidationWarnings] = useState<WarningInfo[]>([]);

  // File input ref + raw File[] tracking for DragDropFileUpload UI
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // Memoized filtered data - only recalculate when originalData, columnFilters, or fileNameFilter change
  const computedFilteredData = useMemo(() => {
    let filtered = originalData;

    // Apply file name filter first
    if (fileNameFilter) {
      filtered = filtered.filter(row => row.fileName === fileNameFilter);
    }

    // Apply each column filter
    Object.entries(columnFilters).forEach(([colIndex, filterValue]) => {
      if (filterValue) {
        filtered = filtered.filter(row => row.columns[parseInt(colIndex)] === filterValue);
      }
    });

    return filtered;
  }, [originalData, columnFilters, fileNameFilter]);

  // Update filteredData when computed value changes
  useEffect(() => {
    setFilteredData(computedFilteredData);
    setCurrentPage(1); // Reset to first page when filters change
  }, [computedFilteredData]);

  // Memoized paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, rowsPerPage]);

  // Total pages
  const totalPages = useMemo(() => {
    return Math.ceil(filteredData.length / rowsPerPage);
  }, [filteredData.length, rowsPerPage]);

  // Debounce filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setColumnFilters(pendingFilters);
    }, DEBOUNCE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [pendingFilters]);

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Clear previous validation messages
    setValidationError('');
    setValidationWarnings([]);

    // Validate each file before processing
    const filesArray = Array.from(files);
    setSelectedFiles(prev => [...prev, ...filesArray]);
    const validationResults = await Promise.all(
      filesArray.map(file => validateFile(file, t, maxFileSizeMB))
    );

    // Check for validation errors
    const errorResults = validationResults.filter(result => !result.isValid);
    if (errorResults.length > 0) {
      setValidationError(errorResults[0].error || 'File validation failed');
      event.target.value = ''; // Reset input
      return;
    }

    // Collect all warnings
    const allWarnings: WarningInfo[] = validationResults.flatMap(
      result => result.warnings ?? (result.warning ? [result.warning] : [])
    );
    if (allWarnings.length > 0) {
      setValidationWarnings(allWarnings);
    }

    setIsLoading(true);
    setLoadingProgress(0);

    let processedCount = 0;

    filesArray.forEach((file) => {
      const sanitizedName = sanitizeFileName(file.name);
      setCurrentFileName(sanitizedName);
      const reader = new FileReader();

      reader.onprogress = (e) => {
        if (e.lengthComputable) {
          const fileProgress = Math.round((e.loaded / e.total) * 100);
          const totalProgress = Math.round(((processedCount + fileProgress / 100) / filesArray.length) * 100);
          setLoadingProgress(totalProgress);
        }
      };

      reader.onload = (e) => {
        const text = e.target?.result as string;
        const rows = text.split('\n').filter(row => row.trim());
        const delimiter = detectDelimiter(rows.slice(0, 5));
        setCsvDelimiter(delimiter);

        const parsedData: CsvRow[] = parseRows(text, delimiter).map(columns => ({
          columns,
          fileName: sanitizedName
        }));

        // Determine column count from first row (avoid spread operator for large arrays)
        if (parsedData.length > 0) {
          const maxCols = parsedData.reduce((max, row) => Math.max(max, row.columns.length), 0);
          setColumnCount(prevCount => Math.max(prevCount, maxCols));
        }

        // Add to existing data
        setOriginalData(prev => [...prev, ...parsedData]);
        setFilteredData(prev => [...prev, ...parsedData]);

        // Add file info
        setUploadedFiles(prev => [...prev, {
          name: sanitizedName,
          size: file.size,
          rowCount: parsedData.length,
          uploadedAt: new Date().toLocaleString()
        }]);

        processedCount++;

        // If all files processed
        if (processedCount === filesArray.length) {
          setTimeout(() => {
            setOriginalData(allData => {
              // Extract unique values for each column using helper function
              const uniqueValuesPerColumn = computeColumnUniqueValues(allData);

              setColumnUniqueValues(uniqueValuesPerColumn);
              setColumnFilters({});
              setIsLoading(false);
              setLoadingProgress(0);
              setCurrentFileName('');
              return allData;
            });
          }, FILE_PROCESSING_DELAY_MS);
        }
      };

      reader.onerror = () => {
        processedCount++;
        if (processedCount === filesArray.length) {
          setIsLoading(false);
          setLoadingProgress(0);
          setCurrentFileName('');
        }
      };

      reader.readAsText(file);
    });
  };

  const handleRemoveFile = useCallback((fileName: string) => {
    // Remove file from uploaded files list
    setUploadedFiles(prev => prev.filter(f => f.name !== fileName));
    setSelectedFiles(prev => prev.filter(f => sanitizeFileName(f.name) !== fileName));

    // Remove data from this file and update unique values
    setOriginalData(prevData => {
      const newData = prevData.filter(row => row.fileName !== fileName);

      // Update unique values for each column using helper function
      const uniqueValuesPerColumn = computeColumnUniqueValues(newData);
      setColumnUniqueValues(uniqueValuesPerColumn);

      if (newData.length === 0) {
        setColumnCount(0);
      }

      return newData;
    });
  }, []);

  const handleRemoveFileByIndex = useCallback((index: number) => {
    const file = selectedFiles[index];
    if (!file) return;
    handleRemoveFile(sanitizeFileName(file.name));
  }, [selectedFiles, handleRemoveFile]);

  const handleClearAll = () => {
    setOriginalData([]);
    setFilteredData([]);
    setUploadedFiles([]);
    setSelectedFiles([]);
    setColumnFilters({});
    setColumnUniqueValues({});
    setColumnCount(0);
    setCsvDelimiter(';');
  };

  const handleColumnFilterChange = useCallback((columnIndex: number, value: string) => {
    setPendingFilters(prev => {
      const newFilters = { ...prev };

      if (value) {
        newFilters[columnIndex] = value;
      } else {
        delete newFilters[columnIndex];
      }

      return newFilters;
    });
  }, []);

  const handleClearColumnFilter = useCallback((columnIndex: number) => {
    setPendingFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[columnIndex];
      return newFilters;
    });

    // Clear search input as well
    setSearchInputs(prev => {
      const newInputs = { ...prev };
      delete newInputs[columnIndex];
      return newInputs;
    });

    // Immediately update actual filters for clear action
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[columnIndex];
      return newFilters;
    });

    // Hide suggestions
    setShowSuggestions(prev => ({ ...prev, [columnIndex]: false }));
  }, []);

  const handleSearchInputChange = useCallback((columnIndex: number, value: string) => {
    setSearchInputs(prev => ({ ...prev, [columnIndex]: value }));
    setShowSuggestions(prev => ({ ...prev, [columnIndex]: value.length > 0 }));
  }, []);

  const handleSearchInputFocus = useCallback((columnIndex: number) => {
    const searchValue = searchInputs[columnIndex] || '';
    if (searchValue.length > 0) {
      setShowSuggestions(prev => ({ ...prev, [columnIndex]: true }));
    }
  }, [searchInputs]);

  const handleSearchInputBlur = useCallback((columnIndex: number) => {
    // Delay hiding to allow click on suggestion
    setTimeout(() => {
      setShowSuggestions(prev => ({ ...prev, [columnIndex]: false }));
    }, 200);
  }, []);

  const handleSelectSuggestion = useCallback((columnIndex: number, value: string) => {
    setSearchInputs(prev => ({ ...prev, [columnIndex]: value }));
    handleColumnFilterChange(columnIndex, value);
    setShowSuggestions(prev => ({ ...prev, [columnIndex]: false }));
  }, [handleColumnFilterChange]);

  const handleClearAllFilters = useCallback(() => {
    setPendingFilters({});
    setColumnFilters({});
    setSearchInputs({});
    setShowSuggestions({});
    setFileNameFilter('');
  }, []);

  const performDownload = (customFileName: string) => {
    const csvContent = buildCsvContent(filteredData, csvDelimiter);

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    // Add .csv extension if not present
    const fileName = customFileName.endsWith('.csv') ? customFileName : `${customFileName}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Revoke the object URL to prevent memory leak
    URL.revokeObjectURL(url);
  };

  const handleDownloadClick = () => {
    if (filteredData.length === 0) return;
    setIsDownloadModalOpen(true);
  };

  const handleDownloadConfirm = (customName: string) => {
    setIsDownloadModalOpen(false);
    performDownload(customName);
  };

  const handleDownloadCancel = () => {
    setIsDownloadModalOpen(false);
  };

  const getDefaultFileName = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    const hasFilters = Object.keys(columnFilters).length > 0;
    return hasFilters
      ? `filtered_data_${timestamp}`
      : `merged_data_${timestamp}`;
  };

  // Chat context publishing
  const chatContext = useMemo(() => {
    const fields: ChatField[] = [];
    fields.push({ label: 'Aktive Spaltenfilter', value: String(Object.values(columnFilters).filter(Boolean).length) });
    if (fileNameFilter) fields.push({ label: 'Dateifilter', value: fileNameFilter });
    const dataProfile = originalData?.length
      ? computeDataProfile(
          originalData.map(r => r.columns),
          Array.from({ length: columnCount }, (_, i) => 'col' + i),
          0,
        )
      : undefined;
    return { fields, dataProfile };
  }, [columnFilters, fileNameFilter, originalData, columnCount]);

  usePublishChatContext(chatContext);

  return (
    <div className="min-h-screen bg-background py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Loading Overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-lg p-4 sm:p-8 max-w-sm sm:max-w-md w-full mx-2 sm:mx-4 shadow-2xl">
              <div className="text-center">
                <div className="mb-3 sm:mb-4">
                  <svg
                    className="animate-spin h-8 w-8 sm:h-12 sm:w-12 mx-auto text-primary"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1.5 sm:mb-2">
                  {t('loading')}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                  {t('processingFile')}
                </p>
                {currentFileName && (
                  <p className="text-[10px] sm:text-xs text-muted-foreground mb-1.5 sm:mb-2 truncate">
                    {currentFileName}
                  </p>
                )}
                {/* Progress Bar */}
                <div className="w-full bg-muted rounded-full h-2 sm:h-2.5 mb-1.5 sm:mb-2">
                  <div
                    className="bg-primary h-2 sm:h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${loadingProgress}%` }}
                  ></div>
                </div>
                <p className="text-[10px] sm:text-xs text-muted-foreground">{loadingProgress}%</p>
              </div>
            </div>
          </div>
        )}

        {/* Download File Name Modal */}
        <DownloadModal
          isOpen={isDownloadModalOpen}
          defaultFileName={getDefaultFileName()}
          onConfirm={handleDownloadConfirm}
          onCancel={handleDownloadCancel}
          t={t}
        />

        {/* Header */}
        <div className="mb-4 sm:mb-8">
          <h1 className="text-xl sm:text-3xl font-bold text-foreground">
            {t('title')}
          </h1>
          <p className="mt-1 sm:mt-2 text-sm sm:text-base text-muted-foreground flex items-center">
            {t('description')}
            <InfoTooltip text={t('tooltips.format')} width="w-96" />
          </p>
        </div>

        {/* Upload Section */}
        <div className="bg-card rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
          <label className="block mb-2 text-xs sm:text-sm font-medium text-foreground">
            {t('uploadLabel')}
          </label>
          <DragDropFileUpload
            onFileChange={handleFileUpload}
            fileInputRef={fileInputRef}
            accept=".csv,.txt"
            multiple
            selectedFiles={selectedFiles}
            onRemoveFile={handleRemoveFileByIndex}
            onClearAll={handleClearAll}
            inputId="csv-filter-upload"
          />

          {/* Validation Error Message */}
          {validationError && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-red-800 mb-1">
                    {t('validation.error')}
                  </h4>
                  <p className="text-sm text-red-700 whitespace-pre-line">
                    {validationError}
                  </p>
                </div>
                <button
                  onClick={() => setValidationError('')}
                  className="ml-4 text-red-600 hover:text-red-800 focus:outline-none"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Validation Warning Message */}
          {validationWarnings.length > 0 && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-yellow-800 mb-1">
                    {t('validation.warning')}
                  </h4>
                  <p className="text-sm text-yellow-700 whitespace-pre-line">
                    {validationWarnings.map(w => renderWarning(t, w)).join('\n')}
                  </p>
                </div>
                <button
                  onClick={() => setValidationWarnings([])}
                  className="ml-4 text-yellow-600 hover:text-yellow-800 focus:outline-none"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Uploaded Files List */}
        {uploadedFiles.length > 0 && (
          <div className="bg-card rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0 mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-foreground">
                {t('uploadedFiles')} ({uploadedFiles.length})
              </h3>
              <button
                onClick={handleClearAll}
                className="px-2.5 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
              >
                {t('clearAll')}
              </button>
            </div>
            <div className="space-y-2">
              {uploadedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 sm:p-3 bg-muted rounded-lg border border-border"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <svg
                        className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-foreground truncate">{file.name}</p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground">
                          {(file.size / 1024).toFixed(2)} KB • {file.rowCount} {t('rows')}
                        </p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveFile(file.name)}
                    className="ml-2 sm:ml-4 p-1 sm:p-1.5 text-red-600 hover:bg-red-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors flex-shrink-0"
                    title={t('removeFile')}
                  >
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter Section */}
        {Object.keys(columnUniqueValues).length > 0 && (
          <div className="bg-card rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="mb-3 sm:mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
              <h3 className="text-base sm:text-lg font-semibold text-foreground">
                {t('filters')}
              </h3>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={handleClearAllFilters}
                  disabled={Object.keys(columnFilters).length === 0 && !fileNameFilter}
                  className="flex-1 sm:flex-none px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted/80 focus:outline-none focus:ring-2 focus:ring-border disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t('clearFilters')}
                </button>
                <button
                  onClick={handleDownloadClick}
                  disabled={filteredData.length === 0}
                  className="flex-1 sm:flex-none px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t('download')}
                </button>
              </div>
            </div>

            {/* File Name Filter */}
            {uploadedFiles.length > 1 && (
              <div className="mb-6 pb-6 border-b border-border">
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('filterByFile')}
                </label>
                <div className="flex gap-2 items-center">
                  <select
                    value={fileNameFilter}
                    onChange={(e) => setFileNameFilter(e.target.value)}
                    className="flex-1 px-3 py-2 text-sm text-foreground border border-input rounded-lg bg-card focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary"
                  >
                    <option value="">{t('allFiles')}</option>
                    {uploadedFiles.map((file, index) => (
                      <option key={index} value={file.name}>
                        {file.name} ({file.rowCount} {t('rows')})
                      </option>
                    ))}
                  </select>
                  {fileNameFilter && (
                    <button
                      onClick={() => setFileNameFilter('')}
                      className="px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      {t('clear')}
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: columnCount }, (_, colIndex) => {
                const uniqueValues = columnUniqueValues[colIndex] || [];
                if (uniqueValues.length === 0) return null;

                const useSearchInput = uniqueValues.length > SEARCH_INPUT_THRESHOLD;
                const searchValue = searchInputs[colIndex] || '';
                const currentFilter = columnFilters[colIndex] || '';

                // For search input: filter and sort suggestions on-demand
                const filteredSuggestions = useSearchInput && searchValue
                  ? uniqueValues
                      .filter(val => val.toLowerCase().includes(searchValue.toLowerCase()))
                      .sort() // Sort only the filtered results
                      .slice(0, MAX_SUGGESTIONS) // Show max suggestions
                  : [];

                return (
                  <div key={colIndex} className="relative">
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-foreground">
                        {t('columnLabel', { index: colIndex + 1 })}
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({t(useSearchInput ? 'valuesCount' : 'optionsCount', { count: uniqueValues.length })})
                        </span>
                      </label>
                      {(currentFilter || searchValue) && (
                        <button
                          onClick={() => handleClearColumnFilter(colIndex)}
                          className="text-xs text-red-600 hover:text-red-700 font-medium"
                        >
                          {t('clear')}
                        </button>
                      )}
                    </div>

                    {useSearchInput ? (
                      /* Search Input for columns with many values */
                      <div className="relative">
                        <div className="relative">
                          <input
                            type="text"
                            value={searchValue}
                            onChange={(e) => handleSearchInputChange(colIndex, e.target.value)}
                            onFocus={() => handleSearchInputFocus(colIndex)}
                            onBlur={() => handleSearchInputBlur(colIndex)}
                            placeholder={currentFilter || t('searchPlaceholder')}
                            className="w-full px-3 py-2 text-sm text-foreground border border-input rounded-lg bg-card focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary pr-8"
                          />
                          <svg
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </div>

                        {/* Suggestions dropdown */}
                        {showSuggestions[colIndex] && filteredSuggestions.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {filteredSuggestions.map((value, index) => (
                              <button
                                key={index}
                                onClick={() => handleSelectSuggestion(colIndex, value)}
                                className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-accent focus:outline-none focus:bg-accent border-b border-border last:border-b-0"
                              >
                                {value}
                              </button>
                            ))}
                          </div>
                        )}

                        {currentFilter && (
                          <p className="mt-1 text-xs text-green-600">
                            ✓ Filtering by: {currentFilter}
                          </p>
                        )}
                      </div>
                    ) : (
                      /* Dropdown for columns with few values */
                      <div>
                        <select
                          value={currentFilter}
                          onChange={(e) => handleColumnFilterChange(colIndex, e.target.value)}
                          className="w-full px-3 py-2 text-sm text-foreground border border-input rounded-lg bg-card focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary"
                        >
                          <option value="">{t('allData')}</option>
                          {uniqueValues.map((value, index) => (
                            <option key={index} value={value}>
                              {value}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Statistics */}
        {filteredData.length > 0 && (
          <div className="bg-card rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
              <div className="flex justify-between items-center sm:block sm:text-center">
                <p className="text-xs sm:text-sm text-muted-foreground">{t('totalRows')}</p>
                <p className="text-base sm:text-2xl font-bold text-foreground">{originalData.length.toLocaleString('de-DE')}</p>
              </div>
              <div className="flex justify-between items-center sm:block sm:text-center">
                <p className="text-xs sm:text-sm text-muted-foreground">{t('filteredRows')}</p>
                <p className="text-base sm:text-2xl font-bold text-primary">{filteredData.length.toLocaleString('de-DE')}</p>
              </div>
              <div className="flex justify-between items-center sm:block sm:text-center">
                <p className="text-xs sm:text-sm text-muted-foreground">{t('activeFilters')}</p>
                <p className="text-base sm:text-2xl font-bold text-foreground">
                  {(Object.keys(columnFilters).length + (fileNameFilter ? 1 : 0)).toLocaleString('de-DE')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        {filteredData.length > 0 && (
          <div className="bg-card rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border" style={{ minWidth: '400px' }}>
                <thead className="bg-muted sticky top-0">
                  <tr>
                    {Array.from({ length: columnCount }, (_, i) => (
                      <th
                        key={i}
                        className="px-2 py-1.5 sm:px-4 sm:py-3 text-left text-[9px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap"
                      >
                        {t('columnShort', { index: i + 1 })}
                      </th>
                    ))}
                    <th className="px-2 py-1.5 sm:px-4 sm:py-3 text-left text-[9px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                      {t('fileName')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {paginatedData.map((row, rowIndex) => (
                    <tr key={rowIndex} className="hover:bg-muted transition-colors">
                      {row.columns.map((col, colIndex) => (
                        <td
                          key={colIndex}
                          className="px-2 py-1.5 sm:px-4 sm:py-3 whitespace-nowrap text-[10px] sm:text-sm text-foreground max-w-[100px] sm:max-w-none truncate"
                        >
                          {col}
                        </td>
                      ))}
                      <td className="px-2 py-1.5 sm:px-4 sm:py-3 whitespace-nowrap text-[10px] sm:text-sm text-primary max-w-[80px] sm:max-w-none truncate">
                        {row.fileName}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination Controls */}
            {filteredData.length > rowsPerPage && (
              <div className="bg-muted px-2 py-2 sm:px-6 sm:py-4 border-t border-border">
                <div className="flex items-center justify-between gap-2">
                  {/* Info & Select */}
                  <div className="flex items-center gap-1.5 sm:gap-4">
                    <span className="text-[9px] sm:text-sm text-muted-foreground whitespace-nowrap">
                      {((currentPage - 1) * rowsPerPage + 1).toLocaleString('de-DE')}-{Math.min(currentPage * rowsPerPage, filteredData.length).toLocaleString('de-DE')}/{filteredData.length.toLocaleString('de-DE')}
                    </span>
                    <select
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="px-1 py-0.5 sm:px-3 sm:py-1.5 text-[10px] sm:text-sm border border-input rounded focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={250}>250</option>
                      <option value={500}>500</option>
                    </select>
                  </div>
                  {/* Navigation */}
                  <div className="flex items-center gap-0.5 sm:gap-2">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center text-[10px] sm:text-sm font-medium text-foreground bg-card border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      «
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center text-[10px] sm:text-sm font-medium text-foreground bg-card border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ‹
                    </button>
                    <span className="px-1 sm:px-3 text-[9px] sm:text-sm text-foreground whitespace-nowrap">
                      {currentPage}/{totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center text-[10px] sm:text-sm font-medium text-foreground bg-card border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ›
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center text-[10px] sm:text-sm font-medium text-foreground bg-card border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      »
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {originalData.length === 0 && (
          <div className="bg-card rounded-lg shadow-md p-8 sm:p-12 text-center">
            <svg
              className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-xs sm:text-sm font-medium text-foreground">
              {t('noData')}
            </h3>
            <p className="mt-1 text-xs sm:text-sm text-muted-foreground">
              {t('uploadPrompt')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
